import { Node } from '@policygenius/dif-ts-types';
import { RepeatableCompositeOutput } from '@policygenius/mortar-dynamic-forms';
import formatResponseError from 'api/util/formatResponseError';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Union } from 'ts-toolbelt';

import { addGlobalError } from 'containers/App/actions';
import isBooleanMultiChoiceQuestion from 'util/interviews/isBooleanMultiChoiceQuestion';

import { DatapointvaluesDPIDValueItem, DatapointvaluesGetResponse } from '../../';
import useApi, { ApiArgs, Error } from '../useApi';
import { RepeatableInstanceDPIDValue } from './types';

export interface UseGetDataPointValuesArgs extends ApiArgs<DatapointvaluesGetResponse> {
  /**
   * By default, useGetDataPointValues will make a request on mount of the hook.
   * Use `lazy: true` to disable that functionality and make the API request manually.
   */
  lazy?: boolean;
  params: {
    urns: string[];
  };
}

export function useGetDataPointValues({
  lazy = false,
  params,
  onCompleted,
  onError,
}: UseGetDataPointValuesArgs) {
  const dispatch = useDispatch();
  const defaultOnError = () => dispatch(addGlobalError());

  const [api, { updateRequest, data, loading, error, activeUserUuid, referenceID }] =
    useApi<DatapointvaluesGetResponse>({
      onCompleted,
      onError: onError ?? defaultOnError,
      initialLoading: !lazy,
    });

  const getDataPointValues = async (): Promise<{
    data: DatapointvaluesGetResponse | null;
    error: Error | null;
  }> => {
    const { urns } = params;

    updateRequest({ status: 'initiate' });

    try {
      const { data: responseData } = await api.dataPointValues.dataPointValuesDetail(
        activeUserUuid,
        /*
          Note: We need to provide the list of urns as a comma-separated-list in a 1 item array
          in order for the request query params to be formatted properly.
          This is because a query param array will be output as a multipart param (i.e. "urns=urn-1&urn-2"),
          but our API requires a comma-separated-list (i.e. "urns=urn-1,urn-2").
          This format allows for the `urns` query param to be properly formatted for our API.
        */
        { urns: [urns.join(',')], referenceID }
      );

      updateRequest({ status: 'complete', payload: responseData });

      return {
        data: responseData,
        error: null,
      };
    } catch (e) {
      const responseError = formatResponseError(e);

      updateRequest({ status: 'error', payload: responseError });

      return {
        data: null,
        error: responseError,
      };
    }
  };

  useEffect(() => {
    if (!lazy) {
      getDataPointValues();
    }
  }, []);

  return {
    data,
    loading,
    error,
    getDataPointValues,
  };
}

export function useCreateDataPointValues({
  onCompleted,
  onError,
}: ApiArgs<DatapointvaluesDPIDValueItem[]> = {}) {
  const dispatch = useDispatch();
  const defaultOnError = () => dispatch(addGlobalError());

  const [api, { updateRequest, loading, error, activeUserUuid, referenceID }] = useApi<
    DatapointvaluesDPIDValueItem[]
  >({
    onCompleted,
    onError: onError ?? defaultOnError,
  });

  const createDataPointValues = async (
    dataPointValues: DatapointvaluesDPIDValueItem[]
  ): Promise<{
    data: DatapointvaluesDPIDValueItem[] | null;
    error: Error | null;
  }> => {
    updateRequest({ status: 'initiate' });

    try {
      await api.dataPointValues.dataPointValuesCreate(
        activeUserUuid,
        {
          dataPointValues,
          referenceID,
        },
        {
          format: 'json',
        }
      );

      updateRequest({ status: 'complete', payload: dataPointValues });

      return {
        data: dataPointValues,
        error: null,
      };
    } catch (e) {
      const responseError = formatResponseError(e);

      updateRequest({ status: 'error', payload: responseError });

      return {
        data: null,
        error: responseError,
      };
    }
  };

  async function createDataPointValuesFromInterview<FormValues extends Record<string, unknown>>(
    values: FormValues,
    schema: Node
  ): Promise<{
    data: DatapointvaluesDPIDValueItem[] | null;
    error: Error | null;
  }> {
    const dataPointValues = buildBodyFromInterview<FormValues>(values, schema);

    return createDataPointValues(dataPointValues);
  }

  return {
    loading,
    error,
    createDataPointValues,
    createDataPointValuesFromInterview,
  };
}

/**
 * Given MDF form values and a schema, generates an array of Data Point Values for use with
 * the POST /data-point-values endpoint.
 *
 * @NOTE: This function does not support Composite/Repeatable Sections
 * @param {FormValues} values MDF FormValues
 * @param {Node} node an interview Node
 * @returns {DatapointvaluesDPIDValueItem}
 */
export function buildBodyFromInterview<FormValues extends Record<string, unknown>>(
  values: FormValues,
  node: Node,
  dataPointItems: DatapointvaluesDPIDValueItem[] = []
) {
  if (node.nodeType === 'QUESTION') {
    if (isBooleanMultiChoiceQuestion(node)) {
      // each choice value is a boolean DPID URN
      // add as separate booleans
      node.choices.forEach(({ value: dpidUrn }) => {
        if (dpidUrn in values) {
          dataPointItems.push({
            dataType: 'boolean',
            urn: dpidUrn as string,
            value: values[dpidUrn],
          });
        }
      });
      // "regular" questions
    } else if (node.dataPoint.urn in values) {
      dataPointItems.push({
        dataType: node.dataPoint.dataType,
        urn: node.dataPoint.urn,
        value: values[node.dataPoint.urn],
      });
    }
  } else if (node.nodeType === 'SECTION' && !node.dataPoint) {
    node.children.forEach((child) => buildBodyFromInterview(values, child, dataPointItems));
    // @NOTE: Non-Repeatable Composite Sections are not yet supported by this function
  } else if (node.nodeType === 'SECTION' && node.dataPoint?.repeatable) {
    const repeatableInstances = values[
      node.dataPoint.urn
    ] as Union.Nullable<RepeatableCompositeOutput>;

    if (Array.isArray(repeatableInstances)) {
      const mappedInstances = repeatableInstances.map<RepeatableInstanceDPIDValue>((instance) => ({
        operation: instance.operation,
        values: Object.entries(instance.values).map(([urn, value]) => ({
          urn,
          dataType:
            urn === 'composite-id' ? 'text' : node.dataPoint!.compositeStructure[urn].dataType,
          value,
        })),
      }));

      dataPointItems.push({
        dataType: node.dataPoint.dataType,
        urn: node.dataPoint.urn,
        value: mappedInstances,
      });
    }
  }

  return dataPointItems;
}
